<template>
  <ReportCard :title="$t('report.clients_revenue_by_card')">
    <template #header>
      <button @click.stop="showRevenueByCardInfo = true"
        class="flex items-center justify-center w-4 h-4 text-gray-400 hover:text-blue-500 transition-colors"
        :title="$t('report.clients_revenue_by_card_info.title')">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4">
          <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
        </svg>
      </button>
    </template>
    <div class="flex items-center justify-end mb-2">
      <TogglePill
        :model-value="chartMode === 'absolute'"
        @update:model-value="chartMode = $event ? 'absolute' : 'percent'"
      >
        <template #icon>
          <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
            <path d="M2 12h10M3 9v3M6 6v6M9 3v9M12 1v11" />
          </svg>
        </template>
        {{ $t('chart.mode_absolute') }}
      </TogglePill>
    </div>
    <div class="ct-legend flex items-center justify-center gap-3 flex-wrap mb-1">
      <span v-for="(name, idx) in cardRevenueChartNames" :key="'crleg-' + name"
        class="flex items-center gap-1.5">
        <span class="w-2 h-2 rounded-full"
          :style="{ backgroundColor: cardColor(name, idx) }"></span>
        <span class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          {{ cardDisplayName(name) }}
        </span>
      </span>
    </div>
    <div class="relative">
      <div ref="chartEl" class="w-full h-64"></div>
      <div v-if="loading"
        class="absolute inset-0 flex items-center justify-center bg-background/60 backdrop-blur-[1px] rounded-lg transition-opacity">
        <Loading />
      </div>
    </div>
  </ReportCard>

  <BottomSheet v-model:is-open="showRevenueByCardInfo" variant="sidebar">
    <ClientsRevenueByCardInfoSheet />
  </BottomSheet>
</template>

<script setup lang="ts">
import { computed, ref, shallowRef, watch, onBeforeUnmount, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import * as echarts from 'echarts'
import type { SalesByClientsResponse } from '../../responses'
import { useAppSettingsStore } from '../../stores/AppSettingsStore'
import { formatCurrencyForCenter } from '../../utils/Languages'
import ReportCard from '../ReportCard.vue'
import Loading from '../Loading.vue'
import TogglePill from '../ui/TogglePill.vue'
import BottomSheet from '../BottomSheet.vue'
import ClientsRevenueByCardInfoSheet from '../ClientsRevenueByCardInfoSheet.vue'
import { useIsDarkMode } from '../../composables/useIsDarkMode'

const props = defineProps<{
  response: SalesByClientsResponse | null
  formatXLabel: (dateString: string) => string
  loading: boolean
  centerId: number
}>()

const { t } = useI18n()
const appSettingsStore = useAppSettingsStore()
const centerObj = computed(() => appSettingsStore.getCenter(props.centerId))

const chartEl = ref<HTMLElement>()
const chartInstance = shallowRef<echarts.ECharts>()
const chartMode = ref<'percent' | 'absolute'>('percent')
const showRevenueByCardInfo = ref(false)

const isDark = useIsDarkMode()

// --- Card helpers ---
const CARD_COLORS = ['#06C8FF', '#F59E0B', '#8B5CF6', '#10B981', '#F43F5E']
const NOT_MEMBER_COLOR = '#C4CAD4'

function cardColor(name: string, idx: number): string {
  if (name === '_not_member') return NOT_MEMBER_COLOR
  return CARD_COLORS[idx % CARD_COLORS.length]
}

function cardDisplayName(name: string): string {
  if (name === '_not_member') return t('report.clients_not_member')
  if (name.toLowerCase() === 'sans carte') return t('report.clients_no_card')
  return name
}

function formatCentimes(val: number | null | undefined): string {
  if (val == null) return '-'
  return formatCurrencyForCenter(val, centerObj.value)
}

function getByCard<T>(byCard: Record<string, T> | never[]): Record<string, T> {
  if (Array.isArray(byCard)) return {}
  return byCard
}

/** Sorted list of card names found across all intervals */
const cardRevenueChartNames = computed((): string[] => {
  if (!props.response?.intervals) return []
  const totals: Record<string, number> = {}
  for (const interval of props.response.intervals) {
    const byCard = getByCard(interval.data.period.by_card)
    for (const [cardName, breakdown] of Object.entries(byCard)) {
      totals[cardName] = (totals[cardName] ?? 0) + breakdown.total_paid
    }
  }
  return Object.keys(totals).sort((a, b) => {
    if (a === '_not_member') return 1
    if (b === '_not_member') return -1
    return (totals[b] ?? 0) - (totals[a] ?? 0)
  })
})

function buildChartOptions(): echarts.EChartsOption {
  if (!props.response?.intervals) return {}

  // Trim trailing empty intervals
  const rawIntervals = props.response.intervals
  let lastIdx = rawIntervals.length - 1
  while (lastIdx >= 0) {
    const byCard = getByCard(rawIntervals[lastIdx].data.period.by_card)
    if (Object.values(byCard).some(b => b.total_paid > 0)) break
    lastIdx--
  }
  const intervals = lastIdx >= 0 ? rawIntervals.slice(0, lastIdx + 1) : rawIntervals

  const xAxisData = intervals.map(i => props.formatXLabel(i.date))
  const cardNames = cardRevenueChartNames.value
  const isPercent = chartMode.value === 'percent'

  const intervalTotals = intervals.map(i => {
    const byCard = getByCard(i.data.period.by_card)
    return Object.values(byCard).reduce((sum, b) => sum + b.total_paid, 0)
  })

  const series: echarts.SeriesOption[] = cardNames.map((cardName, idx) => {
    const rawData = intervals.map(i => {
      const byCard = getByCard(i.data.period.by_card)
      return byCard[cardName]?.total_paid ?? 0
    })

    const data = isPercent
      ? rawData.map((v, i) => intervalTotals[i] > 0 ? (v / intervalTotals[i]) * 100 : 0)
      : rawData

    return {
      type: 'bar',
      name: cardDisplayName(cardName),
      stack: 'cardRevenue',
      data,
      itemStyle: { color: cardColor(cardName, idx) },
      emphasis: { focus: 'series' as const },
    }
  })

  return {
    grid: { left: '2%', right: '2%', bottom: '12%', top: '8%', containLabel: true },
    xAxis: {
      type: 'category',
      data: xAxisData,
      axisLine: { lineStyle: { color: isDark.value ? 'rgba(255,255,255,0.15)' : 'rgba(156,163,175,0.3)' } },
      axisTick: { show: false },
      axisLabel: { color: isDark.value ? 'rgba(255,255,255,0.6)' : 'rgba(75,85,99,0.8)', fontSize: 11 },
    },
    yAxis: isPercent
      ? {
          type: 'value', min: 0, max: 100,
          axisLine: { show: false }, axisTick: { show: false },
          splitLine: { lineStyle: { color: isDark.value ? 'rgba(255,255,255,0.08)' : 'rgba(229,231,235,0.4)', type: 'dashed' } },
          axisLabel: { color: isDark.value ? 'rgba(255,255,255,0.6)' : 'rgba(75,85,99,0.8)', fontSize: 11, formatter: (v: number) => v + '%' },
        }
      : {
          type: 'value',
          axisLine: { show: false }, axisTick: { show: false }, splitNumber: 4,
          splitLine: { lineStyle: { color: isDark.value ? 'rgba(255,255,255,0.08)' : 'rgba(229,231,235,0.4)', type: 'dashed' } },
          axisLabel: { color: isDark.value ? 'rgba(255,255,255,0.6)' : 'rgba(75,85,99,0.8)', fontSize: 11, formatter: (v: number) => formatCentimes(v) },
        },
    tooltip: {
      trigger: 'axis',
      backgroundColor: isDark.value ? 'rgba(40,39,39,0.95)' : 'rgba(255,255,255,0.95)',
      borderColor: isDark.value ? 'rgba(255,255,255,0.1)' : 'rgba(229,231,235,0.8)',
      borderWidth: 1, borderRadius: 8, confine: true,
      textStyle: { color: isDark.value ? 'rgba(255,255,255,0.9)' : 'rgba(17,24,39,0.9)', fontSize: 12 },
      formatter: (params: unknown) => {
        if (!Array.isArray(params) || params.length === 0) return ''
        const dataIdx = params[0].dataIndex as number
        const intervalTotal = intervalTotals[dataIdx] ?? 0
        const displayToKey: Record<string, string> = {}
        for (const cn of cardNames) displayToKey[cardDisplayName(cn)] = cn
        let html = `<div style="font-weight:600;margin-bottom:4px">${params[0].axisValue}</div>`
        for (const p of params) {
          const rawKey = displayToKey[p.seriesName as string] ?? ''
          const byCard = intervals[dataIdx] ? getByCard(intervals[dataIdx].data.period.by_card) : {}
          const rawVal = byCard[rawKey]?.total_paid ?? 0
          const pct = intervalTotal > 0 ? ((rawVal / intervalTotal) * 100).toFixed(1) : '0.0'
          html += `<div style="display:flex;align-items:center;gap:6px;margin-top:2px">${p.marker}<span>${p.seriesName}</span><span style="margin-left:auto;font-weight:600">${formatCentimes(rawVal)}</span><span style="color:rgba(128,128,128,0.8)">(${pct}%)</span></div>`
        }
        html += `<div style="margin-top:4px;padding-top:4px;border-top:1px solid rgba(128,128,128,0.2);font-weight:600">Total: ${formatCentimes(intervalTotal)}</div>`
        return html
      },
    },
    dataZoom: [{ type: 'inside', xAxisIndex: [0] }],
    series,
  }
}

function refreshChart() {
  if (chartInstance.value) {
    chartInstance.value.setOption(buildChartOptions(), true)
  } else if (chartEl.value) {
    chartInstance.value = echarts.init(chartEl.value)
    chartInstance.value.setOption(buildChartOptions())
  }
}

watch(chartMode, () => refreshChart())
watch(isDark, () => refreshChart())

watch(() => props.response, async () => {
  await nextTick()
  refreshChart()
})

watch(chartEl, async (el) => {
  if (el && !chartInstance.value) {
    await nextTick()
    setTimeout(refreshChart, 300)
  }
}, { immediate: true })

onBeforeUnmount(() => {
  chartInstance.value?.dispose()
})

defineExpose({ refreshChart })
</script>
